import { Box, HStack, Icon, Stack, Tag, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiAward, FiTrendingDown } from "react-icons/fi";
import {
  PortalAwardModel,
  WeightType,
  type AwardCriterionDto,
  type PortalSourcingEventDetailDto,
} from "../../../../autogen/bff-api";
import { TipTap, isTiptapContentEmpty } from "../../../../common/input/TipTap/TipTap";
import { usePriceModelDescription } from "../../common/priceModel";
import { effectiveWeight, formatWeightPercent } from "../../buyer/wizard/utils/awardCriteriaStatus";
import { usePortalProcurementTerms } from "../../../../common/terminology/useProcurementTerms";
import { SectionCard } from "./SectionCard";
import { criterionTypeLabelKey } from "../../buyer/wizard/utils/publicProcurementOptions";

// t('of the total')

export const EventAwardCriteriaSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const terms = usePortalProcurementTerms(event);
  if (!event.awardCriteria) return null;

  return (
    <SectionCard icon={FiAward} title={terms.awardCriteria}>
      <AwardCriteriaContent event={event} />
    </SectionCard>
  );
};

export const AwardCriteriaContent = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t } = useTranslation();
  const terms = usePortalProcurementTerms(event);
  const award = event.awardCriteria;
  if (!award) return null;
  const criteria = award.criteria ?? [];

  return (
    <Stack spacing="4">
      <HStack
        bg="smPrimarySofter"
        rounded="md"
        px="4"
        py="3"
        spacing="3"
        align="center"
      >
        <Icon as={FiAward} boxSize="5" color="smPrimary" />
        <Stack spacing="0">
          <Text fontSize="xs" color="smPrimary" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
            {terms.awardModel}
          </Text>
          <Text fontSize="sm" fontWeight="semibold" color="smText">
            {t(award.model)}
          </Text>
        </Stack>
      </HStack>
      {event.priceModel && award.model === PortalAwardModel.BestPriceQualityRatio && (
        <PriceModelInfo priceModel={event.priceModel} />
      )}
      {award.model === PortalAwardModel.LowestPrice ? (
        <Text fontSize="sm" color="smMuted">
          {t("The contract is awarded to the qualifying tender with the lowest price.")}
        </Text>
      ) : (
        <AwardCriteriaList criteria={criteria} />
      )}
    </Stack>
  );
};

const PriceModelInfo = ({ priceModel }: { priceModel: NonNullable<PortalSourcingEventDetailDto["priceModel"]> }) => {
  const { t } = useTranslation();
  const describePriceModel = usePriceModelDescription();
  return (
    <HStack
      bg="smPrimarySofter"
      rounded="md"
      px="4"
      py="3"
      spacing="3"
      align="center"
    >
      <Icon as={FiTrendingDown} boxSize="5" color="smPrimary" />
      <Stack spacing="0">
        <Text fontSize="xs" color="smPrimary" textTransform="uppercase" letterSpacing="wide" fontWeight="semibold">
          {t("Price model")}
        </Text>
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {t(priceModel.type)}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {describePriceModel(priceModel.type, priceModel.breakpointPercent)}
        </Text>
      </Stack>
    </HStack>
  );
};

export const AwardCriteriaList = ({ criteria }: { criteria: AwardCriterionDto[] }) => {
  const { t } = useTranslation();
  if (criteria.length === 0) {
    return (
      <Text fontSize="sm" color="smMuted">
        {t("No criteria have been published for this procurement yet.")}
      </Text>
    );
  }
  return (
    <Stack spacing="3">
      {criteria.map((criterion, index) => {
        const heading = criterion.name?.trim() || t(criterionTypeLabelKey(criterion.type));
        const weight = criterion.weight
          ? criterion.weight.type === WeightType.Percentage
            ? `${criterion.weight.number} %`
            : `${criterion.weight.number} ${t("points")}`
          : null;
        return (
          <Box
            key={`${criterion.type}-${index}`}
            borderWidth="1px"
            borderColor="smBorder"
            rounded="md"
            px="4"
            py="3"
            bg="smBackground"
          >
            <HStack justify="space-between" align="start" spacing="3">
              <Stack spacing="2" flex="1">
                <HStack spacing="2" align="center" flexWrap="wrap">
                  <Tag size="sm" colorScheme="gray" variant="subtle">
                    {t(criterionTypeLabelKey(criterion.type))}
                  </Tag>
                  <Text fontWeight="semibold" fontSize="sm">
                    {heading}
                  </Text>
                </HStack>
                {!isTiptapContentEmpty(criterion.description) && (
                  <TipTap content={criterion.description} editable={false} hasBorder={false} padding="0" maxHeight="" />
                )}
                {/* Suppliers are judged on the sub-criteria, so they belong here with the share of the
                    total each one carries. */}
                {(criterion.subCriteria ?? []).length > 0 && (
                  <Stack spacing="2" pl="3" borderLeftWidth="2px" borderLeftColor="smBorder" mt="1">
                    {criterion.subCriteria?.map((sub, subIndex) => {
                      const effective = effectiveWeight(criterion, sub);
                      return (
                        <Box key={sub.id ?? subIndex}>
                          <HStack spacing="2" align="center" flexWrap="wrap">
                            <Text fontSize="sm" fontWeight="medium">
                              {sub.name?.trim()}
                            </Text>
                            {effective != null && (
                              <Text fontSize="xs" color="smMuted">
                                {formatWeightPercent(effective)} % {t("of the total")}
                              </Text>
                            )}
                          </HStack>
                          {!isTiptapContentEmpty(sub.description) && (
                            <TipTap
                              content={sub.description}
                              editable={false}
                              hasBorder={false}
                              padding="0"
                              maxHeight=""
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
              {weight && (
                <Box
                  bg="smPrimarySofter"
                  color="smPrimary"
                  px="2.5"
                  py="1"
                  rounded="md"
                  fontSize="xs"
                  fontWeight="semibold"
                  flexShrink="0"
                >
                  {weight}
                </Box>
              )}
            </HStack>
          </Box>
        );
      })}
    </Stack>
  );
};
